import axios from 'axios';
import { routeToServerlessAPI } from './errorHandler';

let serverConfig = null;

// Resolves once the permissions request settles, so requests fired on page load
// don't route as if the user had no server.
let resolveServerConfigLoaded;
const serverConfigLoaded = new Promise((resolve) => { resolveServerConfigLoaded = resolve; });
const SERVER_CONFIG_WAIT_MS = 5000;

export const setServerConfig = (config) => {
    serverConfig = config;
    resolveServerConfigLoaded();
};

export const markServerConfigLoaded = () => resolveServerConfigLoaded();

export const getServerConfig = () => serverConfig;

const getApiBaseUrl = (alb) => {
    return `https://api${alb > 1 ? alb : ''}.tradealm.com`;
};

// Returns the user's assigned ECS server URL, or null if they have no server.
const getEcsBaseUrl = async () => {
    if (!serverConfig) {
        await Promise.race([
            serverConfigLoaded,
            new Promise((resolve) => setTimeout(resolve, SERVER_CONFIG_WAIT_MS)),
        ]);
    }
    return serverConfig ? getApiBaseUrl(serverConfig.alb) : null;
};

/**
 * Make an API request with automatic ECS/Lambda routing and failover.
 *
 * During ECS hours (6am-2pm MT weekdays), routes to the user's assigned
 * ECS server via getApiBaseUrl. Users without an assigned server, requests
 * outside those hours, and ECS errors (network/503) go to the Lambda API.
 *
 * @param {string} method - 'get', 'post', or 'put'
 * @param {string} ecsPath - The ECS server path (e.g., '/algo/demo/data/start')
 * @param {string} lambdaPath - The Lambda path (e.g., '/api/v2/data/start')
 * @param {object} options - { headers, params, body }
 * @returns {Promise} Axios response
 */
export const apiRequest = async (method, ecsPath, lambdaPath, { headers, params, body } = {}, useServerlessApi = false) => {
    const useServerless = routeToServerlessAPI();

    const baseUrl = !useServerlessApi && !useServerless ? await getEcsBaseUrl() : null;
    if (baseUrl) {
        const ecsParams = withServerName(params);
        try {
            return await axiosCall(method, `${baseUrl}${ecsPath}`, { headers, params: ecsParams, body });
        } catch (error) {
            if (routeToServerlessAPI(error)) {
                // ECS failed with network error or 503, fall through to serverless
            } else {
                throw error;
            }
        }
    }

    // Serverless (Lambda) fallback
    const serverlessUrl = process.env.REACT_APP_SERVERLESS_API_URL;
    return await axiosCall(method, `${serverlessUrl}${lambdaPath}`, { headers, params, body });
};

/**
 * Make an ECS-only API request (no Lambda fallback).
 * Returns null if ECS is not available (outside hours or no assigned server).
 */
export const ecsRequest = async (method, ecsPath, { headers, params, body } = {}) => {
    if (routeToServerlessAPI()) {
        return null;
    }
    const baseUrl = await getEcsBaseUrl();
    if (!baseUrl) {
        return null;
    }
    return await axiosCall(method, `${baseUrl}${ecsPath}`, { headers, params: withServerName(params), body });
};

// Attach serverName so the ALB can route to the user's target group.
const withServerName = (params) => {
    if (serverConfig && serverConfig.serverName) {
        return { ...params, serverName: serverConfig.serverName };
    }
    return params;
};

const axiosCall = async (method, url, { headers, params, body } = {}) => {
    const config = { headers, params };
    switch (method) {
        case 'get':
            return await axios.get(url, config);
        case 'post':
            return await axios.post(url, body, config);
        case 'put':
            return await axios.put(url, body, config);
        case 'delete':
            return await axios.delete(url, config);
        default:
            throw new Error(`Unsupported HTTP method: ${method}`);
    }
};
